import { Box, Button } from "@mui/material";
import { styled } from "@mui/material/styles";

type KeyboardDisplay = {
  [key: string]: string;
};

const KeyboardContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.charges.backgrounds.lighter,
  borderRadius: theme.spacing(2),
}));

const KeyboardRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  justifyContent: "center",
}));

const KeyButton = styled(Button)(({ theme }) => ({
  minWidth: 110,
  minHeight: 110,
  fontSize: "2.5rem",
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: "white",
  color: theme.palette.text.primary,
  border: "none",
  borderRadius: theme.spacing(1.5),
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "white",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    backgroundColor: theme.palette.charges.borders.light,
    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
    transform: "translateY(0px)",
  },
}));

const SpecialKeyButton = styled(KeyButton)(({ theme }) => ({
  "&:active": {
    backgroundColor: theme.palette.grey[400],
  },
}));

export const CustomKeypad = ({
  value,
  onChange,
  onKeyPress = (button: string) => {},
  layout = {
    default: ["7 8 9", "4 5 6", "1 2 3", "{bksp} 0 {clear}"],
  },
  display = {
    "{bksp}": "⌫",
    "{clear}": "C",
  },
  isDecimal = false,
}: {
  value: string;
  onChange: (input: string) => void;
  onKeyPress?: (button: string) => void;
  layout?: {
    default: string[];
  };
  display?: KeyboardDisplay;
  isDecimal?: boolean;
}) => {
  // Modify display to show '.' for decimal or 'C' for clear
  const modifiedDisplay: KeyboardDisplay = {
    ...display,
    "{clear}": isDecimal ? "." : "C",
  };

  const handleKeyPress = (key: string) => {
    let newInput = value;

    if (key === "{bksp}") {
      newInput = value.slice(0, -1);
    } else if (key === "{clear}") {
      if (isDecimal) {
        if (!value.includes(".")) {
          newInput = value + ".";
        } else {
          onKeyPress(key);
          return;
        }
      } else {
        newInput = "";
      }
    } else {
      newInput = value + key;
    }

    onChange(newInput);
    onKeyPress(key);
  };

  const getKeyDisplay = (key: string): string => {
    return modifiedDisplay[key] || key;
  };

  const isSpecialKey = (key: string): boolean => {
    return key.startsWith("{") && key.endsWith("}");
  };

  const renderRow = (row: string, rowIndex: number) => {
    const keys = row.split(" ");
    return (
      <KeyboardRow key={rowIndex}>
        {keys.map((key, keyIndex) => {
          const displayKey = getKeyDisplay(key);
          const isSpecial = isSpecialKey(key);

          const ButtonComponent = isSpecial ? SpecialKeyButton : KeyButton;

          return (
            <ButtonComponent
              key={`${rowIndex}-${keyIndex}`}
              onClick={() => handleKeyPress(key)}
              variant="contained"
            >
              {displayKey}
            </ButtonComponent>
          );
        })}
      </KeyboardRow>
    );
  };

  return (
    <KeyboardContainer>
      {layout.default.map((row: string, index: number) =>
        renderRow(row, index),
      )}
    </KeyboardContainer>
  );
};
